import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { resolveLegacyTaskType } from 'core/slices/utils/constants';
import { scrollbarStyles } from '../../styleSheet';
import {
  Box,
  Card,
  CardContent,
  Divider,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Pagination,
  Skeleton,
  Typography,
} from '@mui/material';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import Icon from '../tools/Icon';
import SearchField from '../templates/SearchField';

const ROW_HEIGHT = 64; // approximate px per TaskRow
const MIN_PAGE_SIZE = 3;

const styles = {
  card: { width: '100%', height: { xs: 500, md: '60vh' }, maxHeight: 560, boxShadow: 2, display: 'flex', flexDirection: 'column' },
  searchHeader: { py: 2, px: 3, bgcolor: 'background.paper', borderBottom: '1px solid', borderColor: 'divider' },
  listBody: { flexGrow: 1, overflowY: 'auto', pt: 0, px: 0, pb: '0 !important', ...scrollbarStyles },
  paginationFooter: { display: 'flex', justifyContent: 'center', borderTop: '1px solid', borderColor: 'divider', py: 1, flexShrink: 0 },
  taskRow: {
    flexDirection: 'column',
    alignItems: 'stretch',
    py: 1.5,
    transition: 'background 0.15s',
  },
  taskRowHeader: { display: 'flex', alignItems: 'center', px: 3 },
  pendingChip: { mx: 1, px: 0.75, py: 0, minWidth: 24, textAlign: 'center', borderRadius: '4px', flexShrink: 0 },
  pendingChipText: { fontWeight: 700, lineHeight: 1.5, fontSize: { xs: '0.65rem', md: '0.75rem' } },
  taskNameText: { fontSize: { xs: '0.75rem', md: '0.875rem' } },
  progressBar: { mx: 3, mt: 1, borderRadius: 3, height: 6, bgcolor: 'divider' },
  skeletonItem: { py: 0.75, px: 2, flexDirection: 'column', alignItems: 'stretch' },
  skeletonBar: { borderRadius: 3, mt: 0.75, width: '100%' },
};

// Localized name of a task type. The list renders and searches through the same
// helper so a row can always be found by the label the user is reading.
function taskTypeLabel(item, t) {
  return t(`bruno.tasks_names.${resolveLegacyTaskType(item.task_type)}`, item.task_type);
}

// Sort: in progress (pending desc) → completed → no tasks (mirrors mobile TasksTypeList)
function sortTasks(resources, progress) {
  if (!resources?.length || !Object.keys(progress || {}).length) return resources || [];

  const inProgress = [];
  const completed = [];
  const noTasks = [];

  resources.forEach(item => {
    const p = progress[item.task_type];
    if (!p || p.total === 0) {
      noTasks.push(item);
    } else if (p.pending > 0) {
      inProgress.push(item);
    } else {
      completed.push(item);
    }
  });

  inProgress.sort((a, b) => (progress[b.task_type]?.pending || 0) - (progress[a.task_type]?.pending || 0));

  return [...inProgress, ...completed, ...noTasks];
}

function TaskRow({ item, progress, onClick }) {
  const { t } = useTranslation();
  const p = progress[item.task_type] || { total: 0, pending: 0 };
  const completedCount = p.total - p.pending;
  const percentage = p.total > 0 ? (completedCount / p.total) * 100 : 0;
  const disabled = p.total === 0;

  return (
    <ListItem
      disablePadding
      onClick={disabled ? undefined : () => onClick(item.task_type)}
      sx={{
        ...styles.taskRow,
        cursor: disabled ? 'default' : 'pointer',
        '&:hover': disabled ? {} : { bgcolor: 'action.hover' },
      }}
    >
      <Box sx={styles.taskRowHeader}>
        <Icon
          materialIcon={item.material_icon}
          hexColor={disabled ? undefined : item.hex_color}
          color="info"
          variant={disabled ? 'outlined' : 'filled'}
          fontSize="18px"
          sx={{ mx: 0, mr: 1.5, opacity: disabled ? 0.4 : 1, flexShrink: 0 }}
        />

        <ListItemText
          primary={taskTypeLabel(item, t)}
          primaryTypographyProps={{
            variant: 'body2',
            fontWeight: 700,
            color: disabled ? 'text.disabled' : 'text.primary',
            sx: styles.taskNameText,
          }}
          sx={{ flexGrow: 1 }}
        />

        <Box sx={{ ...styles.pendingChip, bgcolor: disabled ? 'action.disabledBackground' : 'divider' }}>
          <Typography variant="caption" sx={{ ...styles.pendingChipText, color: disabled ? 'text.disabled' : 'text.secondary' }}>
            {p.pending}
          </Typography>
        </Box>

        <NavigateNextIcon sx={{ color: disabled ? 'text.disabled' : 'text.disabled', fontSize: 16 }} />
      </Box>

      <LinearProgress
        variant="determinate"
        value={percentage}
        sx={{
          ...styles.progressBar,
          '& .MuiLinearProgress-bar': { bgcolor: disabled ? 'text.disabled' : 'success.main', borderRadius: 3 },
        }}
      />
    </ListItem>
  );
}

export default function TaskProgressList({ loading, resources = [], progress = {} }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const listBodyRef = useRef(null);
  const [pageSize, setPageSize] = useState(MIN_PAGE_SIZE);

  useEffect(() => {
    if (!listBodyRef.current) return;
    const observer = new ResizeObserver(entries => {
      const height = entries[0].contentRect.height;
      setPageSize(Math.max(MIN_PAGE_SIZE, Math.floor(height / ROW_HEIGHT)));
    });
    observer.observe(listBodyRef.current);
    return () => observer.disconnect();
  }, []);

  const handleTaskTypeClick = (taskType) => navigate(`/tasks?type=${taskType}`);

  const visibleResources = Object.keys(progress).length > 0
    ? resources.filter(item => progress[item.task_type] !== undefined)
    : resources;
  const sorted = sortTasks(visibleResources, progress);
  const filtered = sorted.filter(item =>
    taskTypeLabel(item, t).toLowerCase().includes(search.toLowerCase())
  );
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));

  useEffect(() => {
    setPage(prev => Math.min(prev, Math.max(0, totalPages - 1)));
  }, [totalPages]);
  const paginated = filtered.slice(page * pageSize, (page + 1) * pageSize);

  const handleSearch = value => { setSearch(value); setPage(0); };

  return (
    <Card sx={styles.card}>
      <CardContent sx={styles.searchHeader}>
        <SearchField
          value={search}
          onChange={handleSearch}
          placeholder={t('bruno.home.search_tasks', 'Search tasks...')}
        />
      </CardContent>

      <CardContent ref={listBodyRef} sx={styles.listBody}>
        {loading ? (
          <List disablePadding>
            {[...Array(pageSize)].map((_, i) => (
              <ListItem key={i} disablePadding sx={styles.skeletonItem}>
                <Skeleton variant="rounded" width="100%" height={20} />
                <Skeleton variant="rounded" height={6} sx={styles.skeletonBar} />
              </ListItem>
            ))}
          </List>
        ) : (
          <List disablePadding>
            {paginated.map((item, i) => (
              <React.Fragment key={item.task_type}>
                <TaskRow item={item} progress={progress} onClick={handleTaskTypeClick} />
                {i < paginated.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        )}
      </CardContent>

      <Box sx={styles.paginationFooter}>
        <Pagination
          count={totalPages}
          page={page + 1}
          onChange={(_, value) => setPage(value - 1)}
          size="small"
          color="primary"
          siblingCount={1}
          boundaryCount={1}
        />
      </Box>
    </Card>
  );
}
